/// <amd-module name="Tfs/Platform/RestClient/Core" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   tfs\client\core\clientgeneratorconfigs\genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";

import * as Core from "Tfs/Platform/Contracts/Core";
import * as VS_Operations from "VSS/Platform/Contracts/Operations";
import * as VS_Services from "VSS/Platform/Contracts/WebApi";

export interface ICoreRestClient extends IVssRestClient {
    /**
     * Removes the avatar for the project.
     * 
     * @param projectId - The ID or name of the project.
     */
    removeProjectAvatar(projectId: string): Promise<void>;
    /**
     * Sets the avatar for the project.
     * 
     * @param avatarBlob - The avatar blob data object to upload.
     * @param projectId - The ID or name of the project.
     */
    setProjectAvatar(avatarBlob: Core.ProjectAvatar, projectId: string): Promise<void>;
    /**
     * Gets list of user readable teams in a project and teams user is member of (excluded from readable list).
     * 
     * @param projectId - The name or ID (GUID) of the team project containing the teams to retrieve.
     * @param expandIdentity - A value indicating whether or not to expand Identity information in the result WebApiTeam object.
     * @param top - Maximum number of teams to return.
     * @param skip - Number of teams to skip.
     */
    getProjectTeamsByCategory(projectId: string, expandIdentity?: boolean, top?: number, skip?: number): Promise<Core.CategorizedWebApiTeams>;
    /**
     * @param connectedServiceCreationData - 
     * @param projectId - 
     */
    createConnectedService(connectedServiceCreationData: Core.WebApiConnectedServiceDetails, projectId: string): Promise<Core.WebApiConnectedService>;
    /**
     * @param projectId - 
     * @param name - 
     */
    getConnectedServiceDetails(projectId: string, name: string): Promise<Core.WebApiConnectedServiceDetails>;
    /**
     * @param projectId - 
     * @param kind - 
     */
    getConnectedServices(projectId: string, kind?: Core.ConnectedServiceKind): Promise<Core.WebApiConnectedService[]>;
    /**
     * @param mruData - 
     * @param mruName - 
     */
    createIdentityMru(mruData: Core.IdentityData, mruName: string): Promise<void>;
    /**
     * @param mruData - 
     * @param mruName - 
     */
    deleteIdentityMru(mruData: Core.IdentityData, mruName: string): Promise<void>;
    /**
     * @param mruName - 
     */
    getIdentityMru(mruName: string): Promise<VS_Services.IdentityRef[]>;
    /**
     * @param mruData - 
     * @param mruName - 
     */
    updateIdentityMru(mruData: Core.IdentityData, mruName: string): Promise<void>;
    /**
     * Get a list of members for a specific team.
     * 
     * @param projectId - The name or ID (GUID) of the team project the team belongs to.
     * @param teamId - The name or ID (GUID) of the team .
     * @param top - 
     * @param skip - 
     */
    getTeamMembersWithExtendedProperties(projectId: string, teamId: string, top?: number, skip?: number): Promise<VS_Services.TeamMember[]>;
    /**
     * Get a process by ID.
     * 
     * @param processId - ID for a process.
     */
    getProcessById(processId: string): Promise<Core.Process>;
    /**
     * Get a list of processes.
     * 
     */
    getProcesses(): Promise<Core.Process[]>;
    /**
     * Get project collection with the specified id or name.
     * 
     * @param collectionId - 
     */
    getProjectCollection(collectionId: string): Promise<Core.TeamProjectCollection>;
    /**
     * Get project collection references for this application.
     * 
     * @param top - 
     * @param skip - 
     */
    getProjectCollections(top?: number, skip?: number): Promise<Core.TeamProjectCollectionReference[]>;
    /**
     * Gets the history of changes to the project.
     * 
     * @param minRevision - The minimum revision number to return in the history.
     */
    getProjectHistoryEntries(minRevision?: number): Promise<Core.ProjectInfo[]>;
    /**
     * Get project with the specified id or name, optionally including capabilities.
     * 
     * @param projectId - 
     * @param includeCapabilities - Include capabilities (such as source control) in the team project result (default: false).
     * @param includeHistory - Search within renamed projects (that had such name in the past).
     */
    getProject(projectId: string, includeCapabilities?: boolean, includeHistory?: boolean): Promise<Core.TeamProject>;
    /**
     * Get all projects in the organization that the authenticated user has access to.
     * 
     * @param stateFilter - Filter on team projects in a specific team project state (default: WellFormed).
     * @param top - 
     * @param skip - 
     * @param continuationToken - Pointer that shows how many projects already been fetched.
     * @param getDefaultTeamImageUrl - 
     */
    getProjects(stateFilter?: Core.ProjectState, top?: number, skip?: number, continuationToken?: number, getDefaultTeamImageUrl?: boolean): Promise<VS_Services.PagedList<Core.TeamProjectReference>>;
    /**
     * Queues a project to be created. Use the [GetOperation](../../operations/operations/get) to periodically check for create project status.
     * 
     * @param projectToCreate - The project to create.
     */
    queueCreateProject(projectToCreate: Core.TeamProject): Promise<VS_Operations.OperationReference>;
    /**
     * Queues a project to be deleted. Use the [GetOperation](../../operations/operations/get) to periodically check for delete project status.
     * 
     * @param projectId - The project id of the project to delete.
     */
    queueDeleteProject(projectId: string): Promise<VS_Operations.OperationReference>;
    /**
     * Update an existing project's name, abbreviation, description, or restore a project.
     * 
     * @param projectUpdate - The updates for the project. The state must be set to wellFormed to restore the project.
     * @param projectId - The project id of the project to update.
     */
    updateProject(projectUpdate: Core.TeamProject, projectId: string): Promise<VS_Operations.OperationReference>;
    /**
     * Get a collection of team project properties for multiple projects.
     * 
     * @param projectIds - A comma-delimited string of team project IDs
     * @param properties - 
     */
    getProjectsProperties(projectIds: string[], properties?: string[]): Promise<Core.ProjectProperties[]>;
    /**
     * Get a collection of team project properties.
     * 
     * @param projectId - The team project ID.
     * @param keys - A comma-delimited string of team project property names. Wildcard characters ("?" and "*") are supported. If no key is specified, all properties will be returned.
     */
    getProjectProperties(projectId: string, keys?: string[]): Promise<Core.ProjectProperty[]>;
    /**
     * Create, update, and delete team project properties.
     * 
     * @param projectId - The team project ID.
     * @param patchDocument - A JSON Patch document that represents an array of property operations. See RFC 6902 for more details on JSON Patch. The accepted operation verbs are Add and Remove, where Add is used for both creating and updating properties. The path consists of a forward slash and a property name.
     */
    setProjectProperties(projectId: string, patchDocument: VS_Services.JsonPatchDocument): Promise<void>;
    /**
     * @param proxy - 
     */
    createOrUpdateProxy(proxy: Core.Proxy): Promise<Core.Proxy>;
    /**
     * @param proxyUrl - 
     * @param site - 
     */
    deleteProxy(proxyUrl: string, site?: string): Promise<void>;
    /**
     * @param proxyUrl - 
     */
    getProxies(proxyUrl?: string): Promise<Core.Proxy[]>;
    /**
     * Create a team in a team project.
     * 
     * @param team - The team data used to create the team.
     * @param projectId - The name or ID (GUID) of the team project in which to create the team.
     */
    createTeam(team: Core.WebApiTeam, projectId: string): Promise<Core.WebApiTeam>;
    /**
     * Delete a team.
     * 
     * @param projectId - The name or ID (GUID) of the team project containing the team to delete.
     * @param teamId - The name or ID of the team to delete.
     */
    deleteTeam(projectId: string, teamId: string): Promise<void>;
    /**
     * Get a specific team.
     * 
     * @param projectId - The name or ID (GUID) of the team project containing the team.
     * @param teamId - The name or ID (GUID) of the team.
     * @param expandIdentity - A value indicating whether or not to expand Identity information in the result WebApiTeam object.
     */
    getTeam(projectId: string, teamId: string, expandIdentity?: boolean): Promise<Core.WebApiTeam>;
    /**
     * Get a list of teams.
     * 
     * @param projectId - 
     * @param mine - If true return all the teams requesting user is member, otherwise return all the teams user has read access.
     * @param top - Maximum number of teams to return.
     * @param skip - Number of teams to skip.
     * @param expandIdentity - A value indicating whether or not to expand Identity information in the result WebApiTeam object.
     */
    getTeams(projectId: string, mine?: boolean, top?: number, skip?: number, expandIdentity?: boolean): Promise<Core.WebApiTeam[]>;
    /**
     * Update a team's name and/or description.
     * 
     * @param teamData - 
     * @param projectId - The name or ID (GUID) of the team project containing the team to update.
     * @param teamId - The name of ID of the team to update.
     */
    updateTeam(teamData: Core.WebApiTeam, projectId: string, teamId: string): Promise<Core.WebApiTeam>;
    /**
     * Get a list of all teams.
     * 
     * @param mine - If true, then return all teams requesting user is member. Otherwise return all teams user has read access.
     * @param top - Maximum number of teams to return.
     * @param skip - Number of teams to skip.
     * @param expandIdentity - A value indicating whether or not to expand Identity information in the result WebApiTeam object.
     */
    getAllTeams(mine?: boolean, top?: number, skip?: number, expandIdentity?: boolean): Promise<Core.WebApiTeam[]>;
}

class CoreRestClient extends RestClientBase implements ICoreRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Removes the avatar for the project.
     * 
     * @param projectId - The ID or name of the project.
     */
    public async removeProjectAvatar(
        projectId: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "7.1-preview.1",
            method: "DELETE",
            routeTemplate: "_apis/projects/{projectId}/avatar",
            routeValues: {
                projectId: projectId
            }
        });
    }

    /**
     * Sets the avatar for the project.
     * 
     * @param avatarBlob - The avatar blob data object to upload.
     * @param projectId - The ID or name of the project.
     */
    public async setProjectAvatar(
        avatarBlob: Core.ProjectAvatar,
        projectId: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "7.1-preview.1",
            method: "PUT",
            routeTemplate: "_apis/projects/{projectId}/avatar",
            routeValues: {
                projectId: projectId
            },
            body: avatarBlob
        });
    }

    /**
     * Gets list of user readable teams in a project and teams user is member of (excluded from readable list).
     * 
     * @param projectId - The name or ID (GUID) of the team project containing the teams to retrieve.
     * @param expandIdentity - A value indicating whether or not to expand Identity information in the result WebApiTeam object.
     * @param top - Maximum number of teams to return.
     * @param skip - Number of teams to skip.
     */
    public async getProjectTeamsByCategory(
        projectId: string,
        expandIdentity?: boolean,
        top?: number,
        skip?: number
        ): Promise<Core.CategorizedWebApiTeams> {

        const queryValues: any = {
            '$expandIdentity': expandIdentity,
            '$top': top,
            '$skip': skip
        };

        return this.beginRequest<Core.CategorizedWebApiTeams>({
            apiVersion: "7.1-preview.1",
            routeTemplate: "_apis/projects/{projectId}/categorizedTeams/",
            routeValues: {
                projectId: projectId
            },
            queryParams: queryValues
        });
    }

    /**
     * @param connectedServiceCreationData - 
     * @param projectId - 
     */
    public async createConnectedService(
        connectedServiceCreationData: Core.WebApiConnectedServiceDetails,
        projectId: string
        ): Promise<Core.WebApiConnectedService> {

        return this.beginRequest<Core.WebApiConnectedService>({
            apiVersion: "7.1-preview.1",
            method: "POST",
            routeTemplate: "_apis/projects/{projectId}/connectedServices/{name}",
            routeValues: {
                projectId: projectId
            },
            body: connectedServiceCreationData
        });
    }

    /**
     * @param projectId - 
     * @param name - 
     */
    public async getConnectedServiceDetails(
        projectId: string,
        name: string
        ): Promise<Core.WebApiConnectedServiceDetails> {

        return this.beginRequest<Core.WebApiConnectedServiceDetails>({
            apiVersion: "7.1-preview.1",
            routeTemplate: "_apis/projects/{projectId}/connectedServices/{name}",
            routeValues: {
                projectId: projectId,
                name: name
            }
        });
    }

    /**
     * @param projectId - 
     * @param kind - 
     */
    public async getConnectedServices(
        projectId: string,
        kind?: Core.ConnectedServiceKind
        ): Promise<Core.WebApiConnectedService[]> {

        const queryValues: any = {
            kind: kind
        };

        return this.beginRequest<Core.WebApiConnectedService[]>({
            apiVersion: "7.1-preview.1",
            routeTemplate: "_apis/projects/{projectId}/connectedServices/{name}",
            routeValues: {
                projectId: projectId
            },
            queryParams: queryValues
        });
    }

    /**
     * @param mruData - 
     * @param mruName - 
     */
    public async createIdentityMru(
        mruData: Core.IdentityData,
        mruName: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "7.1-preview.1",
            method: "POST",
            routeTemplate: "_apis/core/identityMru/{mruName}",
            routeValues: {
                mruName: mruName
            },
            body: mruData
        });
    }

    /**
     * @param mruData - 
     * @param mruName - 
     */
    public async deleteIdentityMru(
        mruData: Core.IdentityData,
        mruName: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "7.1-preview.1",
            method: "DELETE",
            routeTemplate: "_apis/core/identityMru/{mruName}",
            routeValues: {
                mruName: mruName
            }
        });
    }

    /**
     * @param mruName - 
     */
    public async getIdentityMru(
        mruName: string
        ): Promise<VS_Services.IdentityRef[]> {

        return this.beginRequest<VS_Services.IdentityRef[]>({
            apiVersion: "7.1-preview.1",
            routeTemplate: "_apis/core/identityMru/{mruName}",
            routeValues: {
                mruName: mruName
            }
        });
    }

    /**
     * @param mruData - 
     * @param mruName - 
     */
    public async updateIdentityMru(
        mruData: Core.IdentityData,
        mruName: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "7.1-preview.1",
            method: "PATCH",
            routeTemplate: "_apis/core/identityMru/{mruName}",
            routeValues: {
                mruName: mruName
            },
            body: mruData
        });
    }

    /**
     * Get a list of members for a specific team.
     * 
     * @param projectId - The name or ID (GUID) of the team project the team belongs to.
     * @param teamId - The name or ID (GUID) of the team .
     * @param top - 
     * @param skip - 
     */
    public async getTeamMembersWithExtendedProperties(
        projectId: string,
        teamId: string,
        top?: number,
        skip?: number
        ): Promise<VS_Services.TeamMember[]> {

        const queryValues: any = {
            '$top': top,
            '$skip': skip
        };

        return this.beginRequest<VS_Services.TeamMember[]>({
            apiVersion: "7.1-preview.2",
            routeTemplate: "_apis/projects/{projectId}/teams/{teamId}/members",
            routeValues: {
                projectId: projectId,
                teamId: teamId
            },
            queryParams: queryValues
        });
    }

    /**
     * Get a process by ID.
     * 
     * @param processId - ID for a process.
     */
    public async getProcessById(
        processId: string
        ): Promise<Core.Process> {

        return this.beginRequest<Core.Process>({
            apiVersion: "7.1-preview.1",
            routeTemplate: "_apis/process/processes/{*processId}",
            routeValues: {
                processId: processId
            }
        });
    }

    /**
     * Get a list of processes.
     * 
     */
    public async getProcesses(
        ): Promise<Core.Process[]> {

        return this.beginRequest<Core.Process[]>({
            apiVersion: "7.1-preview.1",
            routeTemplate: "_apis/process/processes/{*processId}"
        });
    }

    /**
     * Get project collection with the specified id or name.
     * 
     * @param collectionId - 
     */
    public async getProjectCollection(
        collectionId: string
        ): Promise<Core.TeamProjectCollection> {

        return this.beginRequest<Core.TeamProjectCollection>({
            apiVersion: "7.1-preview.2",
            routeTemplate: "_apis/projectCollections/{collectionId}",
            routeValues: {
                collectionId: collectionId
            }
        });
    }

    /**
     * Get project collection references for this application.
     * 
     * @param top - 
     * @param skip - 
     */
    public async getProjectCollections(
        top?: number,
        skip?: number
        ): Promise<Core.TeamProjectCollectionReference[]> {

        const queryValues: any = {
            '$top': top,
            '$skip': skip
        };

        return this.beginRequest<Core.TeamProjectCollectionReference[]>({
            apiVersion: "7.1-preview.2",
            routeTemplate: "_apis/projectCollections/{collectionId}",
            queryParams: queryValues
        });
    }

    /**
     * Gets the history of changes to the project.
     * 
     * @param minRevision - The minimum revision number to return in the history.
     */
    public async getProjectHistoryEntries(
        minRevision?: number
        ): Promise<Core.ProjectInfo[]> {

        const queryValues: any = {
            minRevision: minRevision
        };

        return this.beginRequest<Core.ProjectInfo[]>({
            apiVersion: "7.1-preview.2",
            routeTemplate: "_apis/projectHistory",
            queryParams: queryValues
        });
    }

    /**
     * Get project with the specified id or name, optionally including capabilities.
     * 
     * @param projectId - 
     * @param includeCapabilities - Include capabilities (such as source control) in the team project result (default: false).
     * @param includeHistory - Search within renamed projects (that had such name in the past).
     */
    public async getProject(
        projectId: string,
        includeCapabilities?: boolean,
        includeHistory?: boolean
        ): Promise<Core.TeamProject> {

        const queryValues: any = {
            includeCapabilities: includeCapabilities,
            includeHistory: includeHistory
        };

        return this.beginRequest<Core.TeamProject>({
            apiVersion: "7.1-preview.4",
            routeTemplate: "_apis/projects/{*projectId}",
            routeValues: {
                projectId: projectId
            },
            queryParams: queryValues
        });
    }

    /**
     * Get all projects in the organization that the authenticated user has access to.
     * 
     * @param stateFilter - Filter on team projects in a specific team project state (default: WellFormed).
     * @param top - 
     * @param skip - 
     * @param continuationToken - Pointer that shows how many projects already been fetched.
     * @param getDefaultTeamImageUrl - 
     */
    public async getProjects(
        stateFilter?: Core.ProjectState,
        top?: number,
        skip?: number,
        continuationToken?: number,
        getDefaultTeamImageUrl?: boolean
        ): Promise<VS_Services.PagedList<Core.TeamProjectReference>> {

        const queryValues: any = {
            stateFilter: stateFilter,
            '$top': top,
            '$skip': skip,
            continuationToken: continuationToken,
            getDefaultTeamImageUrl: getDefaultTeamImageUrl
        };

        return this.beginRequest<Response>({
            apiVersion: "7.1-preview.4",
            routeTemplate: "_apis/projects/{*projectId}",
            queryParams: queryValues,
            returnRawResponse: true
        }).then(async response => {
            const body = <VS_Services.PagedList<Core.TeamProjectReference>>await response.text().then(deserializeVssJsonObject);
            body.continuationToken = response.headers.get("x-ms-continuationtoken");
            return body;
        });
    }

    /**
     * Queues a project to be created. Use the [GetOperation](../../operations/operations/get) to periodically check for create project status.
     * 
     * @param projectToCreate - The project to create.
     */
    public async queueCreateProject(
        projectToCreate: Core.TeamProject
        ): Promise<VS_Operations.OperationReference> {

        return this.beginRequest<VS_Operations.OperationReference>({
            apiVersion: "7.1-preview.4",
            method: "POST",
            routeTemplate: "_apis/projects/{*projectId}",
            body: projectToCreate
        });
    }

    /**
     * Queues a project to be deleted. Use the [GetOperation](../../operations/operations/get) to periodically check for delete project status.
     * 
     * @param projectId - The project id of the project to delete.
     */
    public async queueDeleteProject(
        projectId: string
        ): Promise<VS_Operations.OperationReference> {

        return this.beginRequest<VS_Operations.OperationReference>({
            apiVersion: "7.1-preview.4",
            method: "DELETE",
            routeTemplate: "_apis/projects/{*projectId}",
            routeValues: {
                projectId: projectId
            }
        });
    }

    /**
     * Update an existing project's name, abbreviation, description, or restore a project.
     * 
     * @param projectUpdate - The updates for the project. The state must be set to wellFormed to restore the project.
     * @param projectId - The project id of the project to update.
     */
    public async updateProject(
        projectUpdate: Core.TeamProject,
        projectId: string
        ): Promise<VS_Operations.OperationReference> {

        return this.beginRequest<VS_Operations.OperationReference>({
            apiVersion: "7.1-preview.4",
            method: "PATCH",
            routeTemplate: "_apis/projects/{*projectId}",
            routeValues: {
                projectId: projectId
            },
            body: projectUpdate
        });
    }

    /**
     * Get a collection of team project properties for multiple projects.
     * 
     * @param projectIds - A comma-delimited string of team project IDs
     * @param properties - 
     */
    public async getProjectsProperties(
        projectIds: string[],
        properties?: string[]
        ): Promise<Core.ProjectProperties[]> {

        const queryValues: any = {
            projectIds: projectIds && projectIds.join(","),
            properties: properties && properties.join(",")
        };

        return this.beginRequest<Core.ProjectProperties[]>({
            apiVersion: "7.1-preview.1",
            routeTemplate: "_apis/projectsproperties",
            queryParams: queryValues
        });
    }

    /**
     * Get a collection of team project properties.
     * 
     * @param projectId - The team project ID.
     * @param keys - A comma-delimited string of team project property names. Wildcard characters ("?" and "*") are supported. If no key is specified, all properties will be returned.
     */
    public async getProjectProperties(
        projectId: string,
        keys?: string[]
        ): Promise<Core.ProjectProperty[]> {

        const queryValues: any = {
            keys: keys && keys.join(",")
        };

        return this.beginRequest<Core.ProjectProperty[]>({
            apiVersion: "7.1-preview.1",
            routeTemplate: "_apis/projects/{projectId}/properties",
            routeValues: {
                projectId: projectId
            },
            queryParams: queryValues
        });
    }

    /**
     * Create, update, and delete team project properties.
     * 
     * @param projectId - The team project ID.
     * @param patchDocument - A JSON Patch document that represents an array of property operations. See RFC 6902 for more details on JSON Patch. The accepted operation verbs are Add and Remove, where Add is used for both creating and updating properties. The path consists of a forward slash and a property name.
     */
    public async setProjectProperties(
        projectId: string,
        patchDocument: VS_Services.JsonPatchDocument
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "7.1-preview.1",
            method: "PATCH",
            routeTemplate: "_apis/projects/{projectId}/properties",
            routeValues: {
                projectId: projectId
            },
            customHeaders: {
                "Content-Type": "application/json-patch+json",
            },
            body: patchDocument
        });
    }

    /**
     * @param proxy - 
     */
    public async createOrUpdateProxy(
        proxy: Core.Proxy
        ): Promise<Core.Proxy> {

        return this.beginRequest<Core.Proxy>({
            apiVersion: "7.1-preview.2",
            method: "PUT",
            routeTemplate: "_apis/proxies",
            body: proxy
        });
    }

    /**
     * @param proxyUrl - 
     * @param site - 
     */
    public async deleteProxy(
        proxyUrl: string,
        site?: string
        ): Promise<void> {

        const queryValues: any = {
            proxyUrl: proxyUrl,
            site: site
        };

        return this.beginRequest<void>({
            apiVersion: "7.1-preview.2",
            method: "DELETE",
            routeTemplate: "_apis/proxies",
            queryParams: queryValues
        });
    }

    /**
     * @param proxyUrl - 
     */
    public async getProxies(
        proxyUrl?: string
        ): Promise<Core.Proxy[]> {

        const queryValues: any = {
            proxyUrl: proxyUrl
        };

        return this.beginRequest<Core.Proxy[]>({
            apiVersion: "7.1-preview.2",
            routeTemplate: "_apis/proxies",
            queryParams: queryValues
        });
    }

    /**
     * Create a team in a team project.
     * 
     * @param team - The team data used to create the team.
     * @param projectId - The name or ID (GUID) of the team project in which to create the team.
     */
    public async createTeam(
        team: Core.WebApiTeam,
        projectId: string
        ): Promise<Core.WebApiTeam> {

        return this.beginRequest<Core.WebApiTeam>({
            apiVersion: "7.1-preview.3",
            method: "POST",
            routeTemplate: "_apis/projects/{projectId}/teams/{*teamId}",
            routeValues: {
                projectId: projectId
            },
            body: team
        });
    }

    /**
     * Delete a team.
     * 
     * @param projectId - The name or ID (GUID) of the team project containing the team to delete.
     * @param teamId - The name or ID of the team to delete.
     */
    public async deleteTeam(
        projectId: string,
        teamId: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "7.1-preview.3",
            method: "DELETE",
            routeTemplate: "_apis/projects/{projectId}/teams/{*teamId}",
            routeValues: {
                projectId: projectId,
                teamId: teamId
            }
        });
    }

    /**
     * Get a specific team.
     * 
     * @param projectId - The name or ID (GUID) of the team project containing the team.
     * @param teamId - The name or ID (GUID) of the team.
     * @param expandIdentity - A value indicating whether or not to expand Identity information in the result WebApiTeam object.
     */
    public async getTeam(
        projectId: string,
        teamId: string,
        expandIdentity?: boolean
        ): Promise<Core.WebApiTeam> {

        const queryValues: any = {
            '$expandIdentity': expandIdentity
        };

        return this.beginRequest<Core.WebApiTeam>({
            apiVersion: "7.1-preview.3",
            routeTemplate: "_apis/projects/{projectId}/teams/{*teamId}",
            routeValues: {
                projectId: projectId,
                teamId: teamId
            },
            queryParams: queryValues
        });
    }

    /**
     * Get a list of teams.
     * 
     * @param projectId - 
     * @param mine - If true return all the teams requesting user is member, otherwise return all the teams user has read access.
     * @param top - Maximum number of teams to return.
     * @param skip - Number of teams to skip.
     * @param expandIdentity - A value indicating whether or not to expand Identity information in the result WebApiTeam object.
     */
    public async getTeams(
        projectId: string,
        mine?: boolean,
        top?: number,
        skip?: number,
        expandIdentity?: boolean
        ): Promise<Core.WebApiTeam[]> {

        const queryValues: any = {
            '$mine': mine,
            '$top': top,
            '$skip': skip,
            '$expandIdentity': expandIdentity
        };

        return this.beginRequest<Core.WebApiTeam[]>({
            apiVersion: "7.1-preview.3",
            routeTemplate: "_apis/projects/{projectId}/teams/{*teamId}",
            routeValues: {
                projectId: projectId
            },
            queryParams: queryValues
        });
    }

    /**
     * Update a team's name and/or description.
     * 
     * @param teamData - 
     * @param projectId - The name or ID (GUID) of the team project containing the team to update.
     * @param teamId - The name of ID of the team to update.
     */
    public async updateTeam(
        teamData: Core.WebApiTeam,
        projectId: string,
        teamId: string
        ): Promise<Core.WebApiTeam> {

        return this.beginRequest<Core.WebApiTeam>({
            apiVersion: "7.1-preview.3",
            method: "PATCH",
            routeTemplate: "_apis/projects/{projectId}/teams/{*teamId}",
            routeValues: {
                projectId: projectId,
                teamId: teamId
            },
            body: teamData
        });
    }

    /**
     * Get a list of all teams.
     * 
     * @param mine - If true, then return all teams requesting user is member. Otherwise return all teams user has read access.
     * @param top - Maximum number of teams to return.
     * @param skip - Number of teams to skip.
     * @param expandIdentity - A value indicating whether or not to expand Identity information in the result WebApiTeam object.
     */
    public async getAllTeams(
        mine?: boolean,
        top?: number,
        skip?: number,
        expandIdentity?: boolean
        ): Promise<Core.WebApiTeam[]> {

        const queryValues: any = {
            '$mine': mine,
            '$top': top,
            '$skip': skip,
            '$expandIdentity': expandIdentity
        };

        return this.beginRequest<Core.WebApiTeam[]>({
            apiVersion: "7.1-preview.3",
            routeTemplate: "_apis/teams",
            queryParams: queryValues
        });
    }

}

export const CoreClientName = "ICoreRestClient";

export function getCoreClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): ICoreRestClient {
    return pageContext.getRestClient<ICoreRestClient>(CoreClientName, options);
}

RestClients.add(CoreClientName, { 
    factory: CoreRestClient,
    options: {
        resourceAreaId: "79134c72-4a58-4b42-976c-04e7115f32bf",
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});
